class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.selectedScope = '';
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.header = document.querySelector('header');
    this.toggleNavigationButton = document.querySelector('.toggle-navigation-button');
    this.form = this.querySelector('form[role="search"]');

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);
    this.searchProductTypes?.addEventListener('change', this.handleProductTypeChange.bind(this));
    this.form?.addEventListener('submit', this.handleFormSubmit.bind(this));

    this.isHeaderSearch = this.closest('header') !== null;

    document.addEventListener('menu-drawer:open', () => {
      this.classList.remove('predictive-search-open');
      document.body.classList.remove('search-open');
    });

    this.states = {
      OPEN: 'predictive-search-open',
      LOADING: 'btn--loading',
      SEARCH_OPEN: 'search-open',
    };
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get searchProductTypes() {
    return this.querySelector('#SearchProductTypes');
  }

  onFocus(event) {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom)}px`
    );
    if (this.isHeaderSearch && !this.searchRecommendationEmpty) {
      document.body.classList.add('search-open');
      this.toggleNavButtonVisibility(false);
    }

    if (!this.searchRecommendationEmpty) {
      this.searchContent.classList.remove('hidden');
    }
    this.classList.add('predictive-search-open');
    if (this.getQuery().length === 0) {
      if (this.searchRecommendationEmpty) {
        this.searchContent.classList.add('hidden');
      }
      return;
    }
    this.crSkuThenRender(); // PM SKU normalize 2026-10-13 (was: this.renderSection(this.setupURL().toString(), event))
  }

  getQuery() {
    return this.input.value.trim();
  }

  clear(event = null) {
    event?.preventDefault();
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    this.toggleSearchState(false);
  }

  handleProductTypeChange(evt) {
    this.selectedScope = evt.target.value;
    const query = this.getQuery();
    if (query.length > 0) {
      const url = this.setupURL().toString();
      this.renderSection(url);
    }
  }

  handleFormSubmit(evt) {
    if (this.selectedScope) {
      evt.preventDefault();
      const query = this.getQuery();
      const scopedQuery = `collection:${this.selectedScope} ${query}`;
      window.location.href = `/search?q=${encodeURIComponent(scopedQuery)}&type=product&options%5Bprefix%5D=last`;
    }
  }

  setupURL() {
    const url = new URL(`${window.shopUrl}${FoxTheme.routes.predictive_search_url}`);
    return (
      // CR-S9: apply theme synonyms (window.crRewrite from snippets/cr-search-enhancements.liquid) to predictive queries
      url.searchParams.set('q', this.crSku && this.crSku.raw === this.getQuery() ? this.crSku.q : window.crRewrite ? window.crRewrite(this.getQuery()) : this.getQuery()),
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3),
      url.searchParams.set('resources[limit_scope]', 'each'),
      // CR-M8: explicitly request pages + articles so Showroom / Free Local Delivery pages appear in the dropdown
      url.searchParams.set('resources[type]', 'product,collection,page,article,query'),
      // CR-S8: include product tags (dashless SKU aliases) in predictive search fields
      url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor,variants.sku,tag'),
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
      url
    );
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    this.crSkuThenRender(); // PM SKU normalize 2026-10-13
  }

  // PM SKU normalize 2026-10-13: model-number queries (window.crSku from snippets/cr-search-enhancements.liquid) pick the
  // Shopify query form with the most variant-SKU matches, then results are re-ordered best SKU match first.
  crSkuThenRender() {
    const raw = this.getQuery();
    const q1 = window.crRewrite ? window.crRewrite(raw) : raw;
    this.crSku = null;
    if (!window.crSku || !window.crSku.modelish(q1)) {
      this.renderSection(this.setupURL().toString());
      return;
    }
    this.setLoadingState(true);
    window.crSku
      .resolve(q1)
      .catch(() => null)
      .then((r) => {
        if (this.getQuery() !== raw) return;
        this.crSku = r ? { raw: raw, key: q1, q: r.q, map: r.map } : null;
        this.renderSection(this.setupURL().toString());
      });
  }

  renderSection(url) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText), this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    this.setLoadingState(true);

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);
      })
      .catch((error) => {
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => this.setLoadingState(false));
  }

  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;
        // PM SKU normalize 2026-10-13: best SKU match first
        if (this.crSku && this.crSku.raw === this.getQuery() && window.crSku) {
          window.crSku.reorder(targetElement.querySelector('#predictive-search-results-products-list'), this.crSku.key, this.crSku.map);
          this.crSkuInject(targetElement, this.crSku); // CR-M48 2026-10-23
        }

        if (this.isHeaderSearch && !document.body.classList.contains('search-open')) {
          document.body.classList.add('search-open');
          this.toggleNavButtonVisibility(true);
          this.classList.add('predictive-search-open');
        } else {
          this.classList.add('predictive-search-open');
        }
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  // CR-M48 (2026-10-23): SKU matches found by window.crSku (search?view=pm-sku) but missing from Shopify's predictive rows
  // (e.g. "9159tlar" -> Delta 9159TL) are fetched as cards (product?view=pm-card) and put first in the dropdown.
  crSkuInject(targetElement, sku) {
    try {
      const box = targetElement.querySelector('#predictive-search-results-products-list');
      const have = {};
      if (box) box.querySelectorAll('a[href*="/products/"]').forEach((a) => { const m = a.getAttribute('href').match(/\/products\/([^/?#]+)/); if (m) have[decodeURIComponent(m[1])] = 1; });
      const miss = Object.keys(sku.map || {})
        .map((h) => ({ h: h, s: window.crSku.score(sku.key, sku.map[h]) }))
        .filter((x) => x.s > 0 && !have[x.h])
        .sort((a, b) => b.s - a.s)
        .slice(0, 3);
      if (!miss.length) return;
      const raw = sku.raw;
      Promise.all(miss.map((x) => fetch('/products/' + encodeURIComponent(x.h) + '?view=pm-card').then((r) => (r.ok ? r.text() : '')).catch(() => ''))).then((cards) => {
        if (this.getQuery() !== raw) return;
        let list = targetElement.querySelector('#predictive-search-results-products-list');
        if (!list) {
          // no product rows at all: build a minimal products block in place of the empty state
          const empty = targetElement.querySelector('.empty-state');
          const wrap = document.createElement('div');
          wrap.className = 'flex flex-col gap-5 lg:gap-6 predictive-search-result search__box-item predictive-search-result--products';
          wrap.innerHTML = '<h4 class="h4 predictive-search-result__heading">Products</h4><div class="swipe-mobile swipe-mobile--2-cols"><div id="predictive-search-results-products-list" class="f-grid f-grid--gap-medium f-grid--row-gap-inherit swipe-mobile__inner"></div></div>';
          if (empty) empty.replaceWith(wrap); else targetElement.prepend(wrap);
          list = wrap.querySelector('#predictive-search-results-products-list');
        }
        const parser = new DOMParser();
        cards.slice().reverse().forEach((html) => {
          if (!html) return;
          const card = parser.parseFromString(html, 'text/html').querySelector('.product-card');
          if (card) list.prepend(card);
        });
      });
    } catch (e) {}
  }

  handleClickOutside(event) {
    const target = event.target;
    const shouldClose = this.isHeaderSearch
      ? !this.contains(target) &&
        ((target.classList.contains('fixed-overlay') && target.closest('.header-section')) ||
          target.classList.contains('header__search-close'))
      : !this.contains(target);

    if (shouldClose) {
      setTimeout(() => this.toggleSearchState(false));
    }
  }

  toggleSearchState(isOpen) {
    this.classList.toggle(this.states.OPEN, isOpen);
    if (this.isHeaderSearch) {
      document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
      this.toggleNavButtonVisibility(!isOpen);
    }
    if (!isOpen && this.searchRecommendationEmpty) {
      this.searchContent?.classList.add('hidden');
    }
  }

  toggleNavButtonVisibility(isOpen) {
    this.toggleNavigationButton && this.toggleNavigationButton.classList.toggle('is-show', isOpen);
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.setAttribute('loading', 'true');
      this.resetButton.classList.add(this.states.LOADING);
    } else {
      this.removeAttribute('loading');
      this.resetButton.classList.remove(this.states.LOADING);
      this.setAttribute('results', 'true');
    }
  }
}
customElements.define('predictive-search', PredictiveSearch);